import React, { useEffect } from 'react';
import useConstant from 'use-constant';
import { useImmer } from 'use-immer';
import produce from 'immer';
import { EffectRainType, EffectType, type SceneEffect } from '@types';
import Title from '@components/Title';
import { Collapse } from '@components/Collapse';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import { Validator } from '@components/Validator';
import { MainPlayer } from '@stores/krpPlayerStores';
import PanelBase from '../../panel/PanelBase';
import { PanelEffectRain } from './PanelEffectRain';
import { EDITING_EFFECT_ID, RAIN_TYPE_CONFIG } from '../constants';
import { PanelEffectSunlight } from './PanelEffectSunlight';
import { toggleSunlightTip } from '../SunlightTip';
import { krpAddEffect } from '../actions/krpEffects';

interface Props {
  data?: SceneEffect;
  onConfirm: (data: SceneEffect, origId?: string) => void;
  onDelete?: (id: string) => void;
  onRequestClose: () => void;
}

const typeItems: DropdownItem[] = [
  { id: EffectType.sunlight, name: '太阳光' },
  { id: EffectType.rain, name: '飘落特效' },
];

export function PanelEffect({
  data,
  onConfirm,
  onDelete,
  onRequestClose,
}: Props) {
  const firstState = useConstant(() =>
    data
      ? produce(data, (draft) => {
          draft.id = EDITING_EFFECT_ID;
        })
      : ({ id: EDITING_EFFECT_ID } as SceneEffect)
  );
  const [state, setState] = useImmer(() => firstState);

  useEffect(() => {
    return () => {
      toggleSunlightTip(false);
    };
  }, []);

  const handleConfirm = () => {
    if (data) {
      onConfirm(state, data.id);
    } else {
      onConfirm(state);
    }
  };

  const handleTypeChange = (typeId: EffectType) => {
    setState((draft) => {
      draft.type = typeId;
      if (draft.type === EffectType.rain) {
        draft.rainType = EffectRainType.rain;
        const setting = RAIN_TYPE_CONFIG[EffectRainType.rain].defaultSetting.sm;
        Object.keys(setting).forEach((key: keyof typeof setting) => {
          draft[key] = setting[key];
        });
      }
      if (draft.type === EffectType.sunlight) {
        const { hlookat = 0, vlookat = 0 } = MainPlayer.get()?.getView() ?? {};
        draft.style = 'blinkstyle4';
        draft.ath = hlookat;
        draft.atv = vlookat;
      }
    });
    toggleSunlightTip(typeId === EffectType.sunlight);
  };

  return (
    <PanelBase
      isChanged={() => firstState !== state}
      title={<Title>添加特效</Title>}
      onRequestClose={onRequestClose}
      onConfirm={handleConfirm}
      onDeleteClick={() => {
        if (data) onDelete?.(data.id);
      }}
      onAfterClose={() => {
        if (data) krpAddEffect(data);
      }}
    >
      <Collapse title="特效类型">
        <Validator
          value={state.type}
          validFn={(value) => value !== undefined}
          warningMessage="请选择特效类型"
        >
          {({ value }) => (
            <Dropdown
              items={typeItems}
              selected={value!}
              onChange={handleTypeChange}
            />
          )}
        </Validator>
      </Collapse>
      {state.type === EffectType.sunlight && (
        <PanelEffectSunlight state={state} setState={setState} />
      )}
      {state.type === EffectType.rain && (
        <PanelEffectRain state={state} setState={setState} />
      )}
    </PanelBase>
  );
}
